import { SimpleSidebar } from "@/core/components/ui/simple-sidebar";
import DismissedSpiffsSidebar from "@/spiffs/common/dismissed-spiffs-sidebar";
import PendingSpiffsSidebar from "@/spiffs/common/pending-spiffs-sidebar";
import { SpiffDetailSidebar } from "@/spiffs/common/spiff-detail-sidebar";
import { SpiffPhotoSidebar } from "@/spiffs/common/spiff-photo-sidebar";
import { SPIFF_SIDEBAR_SECTIONS } from "@/spiffs/constants";
import { useSpiffSidebar } from "@/spiffs/context/spiff-sidebar-context";
import { PayrollPeriodUserSidebar } from "@/spiffs/payroll-periods/payroll-period-user-sidebar";

interface SpiffSidebarProps {
  businessId?: string | null;
}

interface SidebarContentProps {
  section: string;
  selectedItemId: string | null;
  businessId?: string | null;
  resetSidebar: () => void;
  metadata?: {
    payrollPeriod?: import("@/spiffs/spiffs.d").EnrichedGroupedPeriodSpiffs;
  };
}

const SidebarContent = ({
  section,
  selectedItemId,
  businessId,
  resetSidebar,
  metadata,
}: SidebarContentProps) => {
  switch (section) {
    case SPIFF_SIDEBAR_SECTIONS.pendingSpiffs: {
      if (!selectedItemId) {
        resetSidebar();
        return null;
      }
      return (
        <PendingSpiffsSidebar userId={selectedItemId} businessId={businessId} />
      );
    }
    case SPIFF_SIDEBAR_SECTIONS.dismissedSpiffs: {
      if (!selectedItemId) {
        resetSidebar();
        return null;
      }
      return (
        <DismissedSpiffsSidebar
          userId={selectedItemId}
          businessId={businessId}
        />
      );
    }
    case SPIFF_SIDEBAR_SECTIONS.payrollPeriodUser: {
      if (!selectedItemId || !metadata?.payrollPeriod) {
        resetSidebar();
        return null;
      }
      return (
        <PayrollPeriodUserSidebar
          userId={selectedItemId}
          period={metadata.payrollPeriod.period}
          enrichedUsers={metadata.payrollPeriod.enrichedUsers}
          isLocked={metadata.payrollPeriod.isLocked}
          businessId={businessId}
        />
      );
    }
    case SPIFF_SIDEBAR_SECTIONS.spiffPhoto: {
      if (!selectedItemId) {
        resetSidebar();
        return null;
      }
      return (
        <SpiffPhotoSidebar spiffId={selectedItemId} businessId={businessId} />
      );
    }
    case SPIFF_SIDEBAR_SECTIONS.spiffDetail: {
      if (!selectedItemId) {
        resetSidebar();
        return null;
      }
      return (
        <SpiffDetailSidebar spiffId={selectedItemId} businessId={businessId} />
      );
    }
    default:
      return null;
  }
};

export const SpiffSidebar = ({ businessId }: SpiffSidebarProps) => {
  const {
    state: { isOpen, section, selectedItemId, metadata },
    resetSidebar,
  } = useSpiffSidebar();

  if (!isOpen) return <SimpleSidebar />;

  return (
    <SimpleSidebar>
      <SidebarContent
        section={section}
        selectedItemId={selectedItemId}
        businessId={businessId}
        resetSidebar={resetSidebar}
        metadata={metadata}
      />
    </SimpleSidebar>
  );
};
